import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Popover,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { Encaissement, MotifEncaissement, TypeEncaissement, StatusEncaissement } from '../../types/encaissement';
import { Client } from '../../types/client';
import { Devis } from '../../types/devis';
import { ModePaiement } from '../../types/modePaiement';
import { encaissementsAPI, clientsAPI, devisAPI, modesPaiementAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import EncaissementFormDialog from './EncaissementFormDialog';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';

const FINANCIER_COLOR = '#9C27B0';

const ENCAISSEMENT_FILTER_SCHEMA = {
  date:               { type: 'dateRange',   paramFrom: 'date_from', paramTo: 'date_to' },
  echeance:           { type: 'dateRange',   paramFrom: 'echeance_from', paramTo: 'echeance_to' },
  devis_id:           { type: 'autocomplete', param: 'devis_id', transform: (v: string) => parseInt(v) },
  client_id:          { type: 'autocomplete', param: 'client_id', transform: (v: string) => parseInt(v) },
  libelle:            { type: 'text',         param: 'libelle' },
  mode_paiement_id:   { type: 'select',       param: 'mode_paiement_id', transform: (v: string) => parseInt(v) },
  reference_paiement: { type: 'text',         param: 'reference_paiement' },
  montant:            { type: 'numberRange',  paramMin: 'montant_min', paramMax: 'montant_max', transform: (v: string) => parseFloat(v) },
} as const;

const EncaissementList: React.FC = () => {
  const [encaissementsList, setEncaissementsList] = useState<Encaissement[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedEncaissementsList, setDeletedEncaissementsList] = useState<Encaissement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [motifFilter, setMotifFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [totalMontant, setTotalMontant] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editEncaissementId, setEditEncaissementId] = useState<number | undefined>(undefined);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewEncaissement, setPreviewEncaissement] = useState<Encaissement | null>(null);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    ENCAISSEMENT_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const [clientFilterAnchor, setClientFilterAnchor] = useState<HTMLButtonElement | null>(null);
  const [devisFilterAnchor, setDevisFilterAnchor] = useState<HTMLButtonElement | null>(null);

  // Options for select filters
  const [clients, setClients] = useState<Client[]>([]);
  const [devisOptions, setDevisOptions] = useState<Devis[]>([]);
  const [modesPaiement, setModesPaiement] = useState<ModePaiement[]>([]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [clientsData, devisData, modesData] = await Promise.all([
          clientsAPI.getAllForDropdown(),
          devisAPI.getAllForDropdown('valide'),  // not capped at 100
          modesPaiementAPI.getAll(),
        ]);
        setClients(clientsData);
        setDevisOptions(devisData);
        setModesPaiement(modesData);
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchOptions();
  }, []);

  const fetchEncaissements = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;

      // client_id is passed positionally; everything else into the filters bag
      const { client_id, ...filterBag } = toQueryParams();

      const response = await encaissementsAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        (client_id as number | undefined),
        undefined,
        motifFilter || undefined,
        typeFilter || undefined,
        statusFilter || undefined,
        Object.keys(filterBag).length > 0 ? filterBag : undefined
      );
      setEncaissementsList(response.items);
      setTotalCount(response.total);
      setTotalMontant(response.total_montant);
    } catch (err) {
      showError('Erreur lors du chargement des encaissements');
      console.error('Error fetching encaissements:', err);
      setEncaissementsList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, motifFilter, typeFilter, statusFilter, filters]);

  const fetchDeletedEncaissements = async () => {
    try {
      setLoading(true);
      const data = await encaissementsAPI.getDeleted();
      setDeletedEncaissementsList(data);
    } catch (err) {
      showError('Erreur lors du chargement des encaissements supprimés');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchEncaissements();
    }
  }, [fetchEncaissements, tabValue]);

  // Reset page when motif/type/status sub-filters change (column filters reset page via the hook)
  useEffect(() => {
    setPage(0);
  }, [motifFilter, typeFilter, statusFilter]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedEncaissements();
    }
  };

  const handleDelete = async (encaissement: Encaissement) => {
    try {
      await encaissementsAPI.delete(encaissement.id);
      showSuccess('Encaissement supprimé avec succès');
      fetchEncaissements();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleRestore = async (encaissement: Encaissement) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer l'encaissement "${encaissement.reference}" ?`,
      async () => {
        try {
          await encaissementsAPI.restore(encaissement.id);
          showSuccess('Encaissement restauré avec succès');
          fetchDeletedEncaissements();
          fetchEncaissements();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleOpenNewEncaissement = () => {
    setEditEncaissementId(undefined);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleOpenEditEncaissement = (encaissementId: number) => {
    setEditEncaissementId(encaissementId);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setEditEncaissementId(undefined);
  };

  const handleFormSuccess = () => {
    fetchEncaissements();
  };

  const handleOpenPreview = (encaissement: Encaissement) => {
    setPreviewEncaissement(encaissement);
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewEncaissement(null);
  };

  const getMotifChip = (motif: string) => {
    switch (motif) {
      case MotifEncaissement.PAIEMENT_FACTURE:
        return <Chip label="Paiement Facture" color="primary" size="small" />;
      case MotifEncaissement.AVANCE_DEVIS:
        return <Chip label="Avance Devis" color="secondary" size="small" />;
      default:
        return <Chip label={motif} size="small" />;
    }
  };

  const getTypeChip = (type: string) => {
    switch (type) {
      case 'totalite':
        return <Chip label="Totalité" color="success" size="small" variant="outlined" />;
      case 'partiel':
        return <Chip label="Partiel" color="warning" size="small" variant="outlined" />;
      default:
        return <Chip label={type} size="small" variant="outlined" />;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case StatusEncaissement.EN_COURS:
        return <Chip label="En Cours" color="warning" size="small" />;
      case StatusEncaissement.ENCAISSE:
        return <Chip label="Encaissé" color="success" size="small" />;
      case StatusEncaissement.IMPAYE:
        return <Chip label="Impayé" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ py: 3, px: 0, width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestion des Encaissements</Typography>
        {tabValue === 0 && (
          <PermissionButton
            moduleName="Financier"
            operation="add"
            miniModuleName="Encaissements"
            variant="contained"
            onClick={handleOpenNewEncaissement}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
            }}
          >
            Nouvel Encaissement
          </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Encaissements Actifs (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Rechercher par référence ou client..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 350 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Motif</InputLabel>
              <Select
                value={motifFilter}
                label="Motif"
                onChange={(e) => setMotifFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={MotifEncaissement.PAIEMENT_FACTURE}>Paiement Facture</MenuItem>
                <MenuItem value={MotifEncaissement.AVANCE_DEVIS}>Avance Devis</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={TypeEncaissement.TOTALITE}>Totalité</MenuItem>
                <MenuItem value={TypeEncaissement.PARTIEL}>Partiel</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={StatusEncaissement.EN_COURS}>En Cours</MenuItem>
                <MenuItem value={StatusEncaissement.ENCAISSE}>Encaissé</MenuItem>
                <MenuItem value={StatusEncaissement.IMPAYE}>Impayé</MenuItem>
              </Select>
            </FormControl>
            {hasActive() && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={reset}
                sx={{ color: FINANCIER_COLOR }}
              >
                Effacer filtres colonnes
              </Button>
            )}
            <Box sx={{ ml: 'auto', px: 2, py: 0.5, borderRadius: 1, backgroundColor: 'rgba(156, 39, 176, 0.08)', border: '1px solid rgba(156, 39, 176, 0.2)' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: FINANCIER_COLOR, whiteSpace: 'nowrap' }}>
                Total: {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
              </Typography>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={getModuleTableTheme('financier')}>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 95 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date
                      <ColumnFilter
                        type="dateRange"
                        value={filters.date}
                        onChange={(val) => update('date', val)}
                        label="Date"
                        color={FINANCIER_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Sources
                      <IconButton
                        size="small"
                        onClick={(e) => setClientFilterAnchor(e.currentTarget)}
                        sx={{
                          ml: 0.5,
                          p: 0.25,
                          color: filters.client_id ? FINANCIER_COLOR : 'inherit',
                          opacity: filters.client_id ? 1 : 0.5,
                          '&:hover': { opacity: 1, color: FINANCIER_COLOR },
                        }}
                      >
                        <FilterIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Popover
                        open={Boolean(clientFilterAnchor)}
                        anchorEl={clientFilterAnchor}
                        onClose={() => setClientFilterAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      >
                        <Box sx={{ p: 2, minWidth: 280 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                            Filtrer par Client
                          </Typography>
                          <Autocomplete
                            size="small"
                            options={clients.map(c => ({ value: c.id.toString(), label: c.name }))}
                            getOptionLabel={(option) => option.label}
                            value={clients.map(c => ({ value: c.id.toString(), label: c.name })).find(o => o.value === filters.client_id) || null}
                            onChange={(_, newValue) => update('client_id', newValue?.value || '')}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Client"
                                placeholder="Rechercher..."
                              />
                            )}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                          />
                        </Box>
                      </Popover>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Devis/Facture
                      <IconButton
                        size="small"
                        onClick={(e) => setDevisFilterAnchor(e.currentTarget)}
                        sx={{
                          ml: 0.5,
                          p: 0.25,
                          color: filters.devis_id ? FINANCIER_COLOR : 'inherit',
                          opacity: filters.devis_id ? 1 : 0.5,
                          '&:hover': { opacity: 1, color: FINANCIER_COLOR },
                        }}
                      >
                        <FilterIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Popover
                        open={Boolean(devisFilterAnchor)}
                        anchorEl={devisFilterAnchor}
                        onClose={() => setDevisFilterAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      >
                        <Box sx={{ p: 2, minWidth: 280 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                            Filtrer par Devis
                          </Typography>
                          <Autocomplete
                            size="small"
                            options={devisOptions.map(d => ({ value: d.id.toString(), label: d.reference }))}
                            getOptionLabel={(option) => option.label}
                            value={devisOptions.map(d => ({ value: d.id.toString(), label: d.reference })).find(o => o.value === filters.devis_id) || null}
                            onChange={(_, newValue) => update('devis_id', newValue?.value || '')}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Devis"
                                placeholder="Rechercher..."
                              />
                            )}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                          />
                        </Box>
                      </Popover>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Libellé
                      <ColumnFilter
                        type="text"
                        value={filters.libelle}
                        onChange={(val) => update('libelle', val)}
                        label="Libellé"
                        placeholder="Rechercher libellé..."
                        color={FINANCIER_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 110 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Mode Paiement
                      <ColumnFilter
                        type="select"
                        value={filters.mode_paiement_id}
                        onChange={(val) => update('mode_paiement_id', val)}
                        options={modesPaiement.map(m => ({ value: m.id.toString(), label: m.nom }))}
                        label="Mode Paiement"
                        color={FINANCIER_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 110 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Réf. Paiement
                      <ColumnFilter
                        type="text"
                        value={filters.reference_paiement}
                        onChange={(val) => update('reference_paiement', val)}
                        label="Réf. Paiement"
                        placeholder="Rechercher..."
                        color={FINANCIER_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Montant
                      <ColumnFilter
                        type="numberRange"
                        value={filters.montant}
                        onChange={(val) => update('montant', val)}
                        label="Montant"
                        color={FINANCIER_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 95 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Échéance
                      <ColumnFilter
                        type="dateRange"
                        value={filters.echeance}
                        onChange={(val) => update('echeance', val)}
                        label="Échéance"
                        color={FINANCIER_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>Statut</TableCell>
                  <TableCell align="center" sx={{ width: 130 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : encaissementsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Aucun encaissement trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  encaissementsList.map((encaissement) => (
                    <TableRow key={encaissement.id} hover>
                      {/* Date */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(encaissement.date).toLocaleDateString('fr-FR')}
                      </TableCell>
                      {/* Sources */}
                      <TableCell>
                        <Tooltip title={encaissement.client_name || ''} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {encaissement.client_name || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* Devis/Facture */}
                      <TableCell>
                        <Tooltip
                          title={
                            encaissement.motif_encaissement === MotifEncaissement.PAIEMENT_FACTURE
                              ? encaissement.facture_reference || ''
                              : encaissement.devis_reference || ''
                          }
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 500,
                            }}
                          >
                            {encaissement.motif_encaissement === MotifEncaissement.PAIEMENT_FACTURE
                              ? encaissement.facture_reference || '-'
                              : encaissement.devis_reference || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* Libellé */}
                      <TableCell>
                        <Tooltip title={encaissement.libelle || ''} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {encaissement.libelle || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* Mode Paiement */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {encaissement.mode_paiement_nom || '-'}
                        </Typography>
                      </TableCell>
                      {/* Réf. Paiement */}
                      <TableCell>
                        <Tooltip title={encaissement.reference_paiement || ''} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {encaissement.reference_paiement || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* Montant */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {Number(encaissement.montant_encaisse).toLocaleString('fr-FR', {
                          minimumFractionDigits: 2,
                        })}{' '}
                        DH
                      </TableCell>
                      {/* Échéance */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {encaissement.date_echeance ? new Date(encaissement.date_echeance).toLocaleDateString('fr-FR') : '-'}
                      </TableCell>
                      {/* Statut */}
                      <TableCell>
                        {getStatusChip(encaissement.status)}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Voir détails" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenPreview(encaissement)}
                            sx={{ color: '#9C27B0' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <PermissionButton
                          moduleName="Financier"
                          operation="edit"
                          miniModuleName="Encaissements"
                          component="IconButton"
                          onClick={() => handleOpenEditEncaissement(encaissement.id)}
                        >
                          <EditIcon />
                        </PermissionButton>
                        <PermissionButton
                          moduleName="Financier"
                          operation="delete"
                          miniModuleName="Encaissements"
                          component="IconButton"
                          onClick={() => {
                            showConfirm(
                              `Êtes-vous sûr de vouloir supprimer l'encaissement "${encaissement.reference}" ?`,
                              () => handleDelete(encaissement),
                              {
                                title: 'Confirmer la suppression',
                                confirmText: 'Supprimer',
                                cancelText: 'Annuler',
                              }
                            );
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </PermissionButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('financier')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Référence</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Motif</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedEncaissementsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucun encaissement supprimé
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedEncaissementsList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((encaissement) => (
                      <TableRow key={encaissement.id}>
                        <TableCell>
                          {new Date(encaissement.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>{encaissement.reference}</TableCell>
                        <TableCell>{encaissement.client_name || '-'}</TableCell>
                        <TableCell>{getMotifChip(encaissement.motif_encaissement)}</TableCell>
                        <TableCell align="right">
                          {Number(encaissement.montant_encaisse).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell>
                          {encaissement.deleted_at
                            ? new Date(encaissement.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRestore(encaissement)}
                            color="primary"
                            title="Restaurer"
                          >
                            <RestoreIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedEncaissementsList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Form Dialog */}
      <EncaissementFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSuccess={handleFormSuccess}
        encaissementId={editEncaissementId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#9C27B0', color: 'white' }}>
          Détails de l'Encaissement
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {previewEncaissement && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Référence</Typography>
                <Typography variant="body1" fontWeight={500}>{previewEncaissement.reference}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                <Typography variant="body1">{new Date(previewEncaissement.date).toLocaleDateString('fr-FR')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date d'Échéance</Typography>
                <Typography variant="body1">{previewEncaissement.date_echeance ? new Date(previewEncaissement.date_echeance).toLocaleDateString('fr-FR') : '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Client (Sources)</Typography>
                <Typography variant="body1">{previewEncaissement.client_name || '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Mode de Paiement</Typography>
                <Typography variant="body1">{previewEncaissement.mode_paiement_nom || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Référence de Paiement</Typography>
                <Typography variant="body1">{previewEncaissement.reference_paiement || '-'}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Type d'Encaissement</Typography>
                <Box sx={{ mt: 0.5 }}>{getTypeChip(previewEncaissement.type_encaissement)}</Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Motif d'Encaissement</Typography>
                <Box sx={{ mt: 0.5 }}>{getMotifChip(previewEncaissement.motif_encaissement)}</Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(previewEncaissement.status)}</Box>
              </Grid>

              {previewEncaissement.motif_encaissement === MotifEncaissement.PAIEMENT_FACTURE && previewEncaissement.facture_reference && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Facture</Typography>
                  <Typography variant="body1">{previewEncaissement.facture_reference}</Typography>
                </Grid>
              )}

              {previewEncaissement.motif_encaissement === MotifEncaissement.AVANCE_DEVIS && previewEncaissement.devis_reference && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Devis</Typography>
                  <Typography variant="body1">{previewEncaissement.devis_reference}</Typography>
                </Grid>
              )}

              {previewEncaissement.motif_encaissement === MotifEncaissement.AVANCE_DEVIS && previewEncaissement.projet_nom && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Projet</Typography>
                  <Typography variant="body1">{previewEncaissement.projet_nom}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Libellé</Typography>
                <Typography variant="body1">{previewEncaissement.libelle || '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Montant Encaissé</Typography>
                <Typography variant="h6" color="primary" fontWeight={600}>
                  {Number(previewEncaissement.montant_encaisse).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Reste</Typography>
                <Typography variant="h6" fontWeight={500}>
                  {previewEncaissement.reste !== null
                    ? `${Number(previewEncaissement.reste).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`
                    : '-'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Créé par</Typography>
                <Typography variant="body1">{previewEncaissement.user_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date de création</Typography>
                <Typography variant="body1">
                  {previewEncaissement.created_at
                    ? new Date(previewEncaissement.created_at).toLocaleDateString('fr-FR')
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePreview} variant="outlined">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncaissementList;
